import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu'
import ThemeToggle from '@shared/components/ThemeToggle'
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/avatar'
import { useShopConfig } from '@/hooks/use-shop-config'
import { tenantAPI } from '@/lib/api'

const Navbar: React.FC = () => {
  const { currentUser, userProfile, logout, loading } = useAuth()
  const [displayName, setDisplayName] = useState<string>('')
  const { config } = useShopConfig()
  const shopName = (config?.name && config.name.trim()) ? config.name : 'Motiv'
  const [tenantStatus, setTenantStatus] = useState<string | null>(null)
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null)
  // Removed in favor of platform dashboard Businesses tab

  useEffect(() => {
    // Only load tenant settings if user is authenticated and not loading
    if (!currentUser || loading) return

    async function loadTenant() {
      try {
        const data = await tenantAPI.getSettings()
        const status = data?.tenant?.status as string | undefined
        const createdAt = data?.tenant?.created_at as string | undefined
        setTenantStatus(status || null)
        if (status === 'trial' && createdAt) {
          const created = new Date(createdAt).getTime()
          const expires = created + 14 * 24 * 60 * 60 * 1000
          const now = Date.now()
          const days = Math.max(0, Math.ceil((expires - now) / (24 * 60 * 60 * 1000)))
          setTrialDaysLeft(days)
        } else {
          setTrialDaysLeft(null)
        }
      } catch (error) {
        console.error('Failed to load tenant settings:', error)
      }
    }
    loadTenant()
  }, [currentUser, loading])

  // Tenant switching moved to platform dashboard

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!currentUser) { 
        setDisplayName(''); 
        return 
      }
      const name = userProfile?.display_name || currentUser.email || ''
      setDisplayName(name)
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [currentUser, userProfile?.display_name, userProfile?.title, currentUser?.email])

  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : 'U'

  return (
    <div className="sticky top-0 w-full z-20">
      {tenantStatus === 'trial' && trialDaysLeft !== null && (
        <div className="w-full bg-yellow-100 text-yellow-900 text-center text-sm py-1 border-b border-yellow-200">
          You are using a trial of Motiv. {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} remaining.
        </div>
      )}
      <div className="w-full h-16 bg-background border-b border-border flex items-center justify-between px-4">
        {/* Mobile: Position Motiv text to the right of menu button */}
        <div className="md:hidden ml-16">
          <Link to="/" className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-primary-600">{shopName}</h1>
            </div>
          </Link>
        </div>

        {/* Desktop: Normal positioning */}
        <div className="hidden md:block">
          <Link to="/" className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-primary-600">{shopName}</h1>
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Tenant switcher removed to reduce confusion */}
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 rounded-full px-2 py-1 hover:bg-accent">
                <div className="h-8 w-8">
                  <Avatar className="h-8 w-8">
                    {userProfile?.profile_picture_url ? (
                      <AvatarImage src={userProfile.profile_picture_url} />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground">{userInitial}</AvatarFallback>
                    )}
                  </Avatar>
                </div>
                <div className="hidden sm:block text-sm text-left">
                  <div className="font-medium leading-none text-foreground">{displayName || 'User'}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover text-popover-foreground border border-border shadow-md">
              <DropdownMenuItem asChild>
                <Link to="/account-settings">Account Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-500">Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}

export default Navbar